import { UnauthorizedError } from '@/domain/errors/unauthorized-error.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type {
    AiChatAttachmentRef,
    AiChatElicitationAnswer,
    AiChatEventHandlers,
    AiChatEventsSubscription,
    AiChatGrant,
    AiChatHistory,
    AiChatModelsCatalog,
    AiChatRepository
} from '@/domain/repositories/ai-chat-repository.js';
import type { EventSourceClient } from '@/infra/http/event-source-client.js';

const CHAT_GRANT_EXPIRED_MESSAGE = 'Sessão do chat expirou. Recarregue a página e tente novamente.';

export class AiChatRepositoryImpl implements AiChatRepository {
    private readonly grantInFlight = new Map<string, Promise<AiChatGrant>>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly eventSourceClient: EventSourceClient
    ) {}

    public async mintGrant(developmentId: string): Promise<AiChatGrant> {
        const existing = this.grantInFlight.get(developmentId);
        if (existing) {
            return existing;
        }
        const pending = this.httpClient
            .request<AiChatGrant>({
                url: `/api/developments/${encodeURIComponent(developmentId)}/ai-chat/grant`,
                method: 'POST',
                credentials: 'include'
            })
            .then((response) => response.body)
            .finally(() => {
                this.grantInFlight.delete(developmentId);
            });
        this.grantInFlight.set(developmentId, pending);
        return pending;
    }

    public async getHistory(developmentId: string): Promise<AiChatHistory> {
        const response = await this.httpClient.request<AiChatHistory>({
            url: `/agent-ef/developments/${encodeURIComponent(developmentId)}/chat/messages`,
            method: 'GET',
            credentials: 'include'
        });
        return response.body;
    }

    public async listModels(): Promise<AiChatModelsCatalog> {
        const response = await this.httpClient.request<AiChatModelsCatalog>({
            url: '/agent-ef/models',
            method: 'GET',
            credentials: 'include'
        });
        return response.body;
    }

    public async sendMessage(developmentId: string, content: string, attachments?: AiChatAttachmentRef[], model?: string): Promise<void> {
        const body: { content: string; attachments?: AiChatAttachmentRef[]; model?: string } = { content };
        if (attachments && attachments.length > 0) {
            body.attachments = attachments;
        }
        if (model) {
            body.model = model;
        }
        await this.withFreshGrant(developmentId, () =>
            this.httpClient.request<{ accepted: boolean }>({
                url: `/agent-ef/developments/${encodeURIComponent(developmentId)}/chat/messages`,
                method: 'POST',
                credentials: 'include',
                body
            })
        );
    }

    public async uploadChatAttachment(developmentId: string, file: File): Promise<AiChatAttachmentRef> {
        const formData = new FormData();
        formData.append('file', file);
        const response = await this.httpClient.request<AiChatAttachmentRef>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-chat/attachments`,
            method: 'POST',
            credentials: 'include',
            body: formData
        });
        return response.body;
    }

    public async getChatAttachmentDownloadUrl(developmentId: string, fileUrl: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-chat/attachments/download?key=${encodeURIComponent(fileUrl)}`,
            method: 'GET',
            credentials: 'include'
        });
        return response.body;
    }

    public async startAnalysis(developmentId: string, instruction?: string, attachments?: AiChatAttachmentRef[]): Promise<void> {
        const trimmed = instruction?.trim();
        const body: { instruction?: string; attachments?: AiChatAttachmentRef[] } = {};
        if (trimmed) {
            body.instruction = trimmed;
        }
        if (attachments && attachments.length > 0) {
            body.attachments = attachments;
        }
        await this.httpClient.request<{ message: string; attemptNumber: number }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-analysis/start`,
            method: 'POST',
            credentials: 'include',
            body
        });
    }

    public async cancelAnalysis(developmentId: string): Promise<void> {
        await this.httpClient.request<{ message: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-analysis/cancel`,
            method: 'POST',
            credentials: 'include'
        });
    }

    public async resolveElicitation(developmentId: string, toolCallId: string, answer: AiChatElicitationAnswer): Promise<void> {
        await this.withFreshGrant(developmentId, () =>
            this.httpClient.request<{ resolved: boolean }>({
                url: `/agent-ef/developments/${encodeURIComponent(developmentId)}/chat/elicitation/resolve`,
                method: 'POST',
                credentials: 'include',
                body: { toolCallId, optionIds: answer.optionIds, freeText: answer.freeText, answers: answer.answers }
            })
        );
    }

    public openEvents(developmentId: string, handlers: AiChatEventHandlers): AiChatEventsSubscription {
        return this.eventSourceClient.open({
            url: `/agent-ef/developments/${encodeURIComponent(developmentId)}/chat/events`,
            withCredentials: true,
            handlers: {
                onEvent: handlers.onEvent,
                onError: (error) => {
                    void this.mintGrant(developmentId).catch(() => undefined);
                    handlers.onError?.(error);
                }
            }
        });
    }

    private async withFreshGrant<T>(developmentId: string, operation: () => Promise<T>): Promise<T> {
        await this.mintGrant(developmentId);
        try {
            return await operation();
        } catch (error) {
            if (!(error instanceof UnauthorizedError)) {
                throw error;
            }
            await this.mintGrant(developmentId);
            try {
                return await operation();
            } catch (retryError) {
                if (retryError instanceof UnauthorizedError) {
                    throw new UnauthorizedError(CHAT_GRANT_EXPIRED_MESSAGE);
                }
                throw retryError;
            }
        }
    }
}
